import React, { useState, useCallback, useRef } from "react";
import { Share2, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Floating "Copy my share link" button shown in the persistent header for
 * every signed-in account. Lazy-fetches the user's referral URL on first
 * click (cached for the rest of the session), copies to clipboard, and
 * flashes a "Copied!" pulse.
 *
 * Implementation notes:
 *  - Uses the shorter `short_url` returned by `/api/referrals/me` so the
 *    rendered link is always `isaiahsworld.com/r/<code>`, matches the
 *    format used in every email.
 *  - Falls back to `navigator.clipboard.writeText` first, then a textarea
 *    `execCommand('copy')` shim for older browsers / iOS Safari quirks.
 *  - Mobile-friendly: icon-only button on `< sm`, full label on `sm+`.
 */
export default function CopyShareLinkButton() {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const cachedUrlRef = useRef(null);

  const _copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) { /* fall through to legacy shim */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (e) { return false; }
  };

  const handleCopy = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      let url = cachedUrlRef.current;
      if (!url) {
        // Retry a couple of times: this endpoint occasionally 5xx's on a cold
        // request (Cloudflare can't parse a slow/empty origin response), and a
        // quick retry almost always succeeds — far better than a scary error.
        let r, lastErr;
        for (let attempt = 0; attempt < 3 && !r; attempt++) {
          try {
            r = await axios.get(`${API}/referrals/me`, { timeout: 15000 });
          } catch (err) {
            lastErr = err;
            const status = err.response?.status;
            // Retry only on transient failures (5xx / network / timeout).
            if (status && status < 500 && status !== 429) throw err;
            await new Promise((res) => setTimeout(res, 700 * (attempt + 1)));
          }
        }
        if (!r) throw lastErr || new Error("No share link found");
        url = r.data?.short_url || r.data?.short_base_url || r.data?.url || r.data?.base_url;
        cachedUrlRef.current = url;
      }
      if (!url) throw new Error("No share link found");

      // Mobile / supported browsers → trigger the OS share sheet (iMessage,
      // WhatsApp, AirDrop, etc.) for dramatically higher conversion than
      // copy-to-clipboard. Desktop falls back to clipboard copy.
      // The cancellation `AbortError` is silently swallowed (user closed the
      // sheet), only real failures fall through to the clipboard path.
      const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
      if (canShare) {
        try {
          await navigator.share({
            title: "Isaiah's World",
            text: "Hey, check out Isaiah's World, my kids love it:",
            url,
          });
          // Successful share counts as a "copy" UX-wise.
          setCopied(true);
          toast.success("Shared!", { description: url, duration: 2000 });
          setTimeout(() => setCopied(false), 1800);
          return;
        } catch (e) {
          // User aborted the share sheet, silently fall through to clipboard.
          // Any other error also falls through so the user always gets the link.
          if (e && e.name === "AbortError") {
            return;
          }
        }
      }

      const ok = await _copyToClipboard(url);
      if (!ok) throw new Error("Couldn't copy");
      setCopied(true);
      toast.success("Share link copied!", { description: url, duration: 2400 });
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // Never surface raw origin/Cloudflare HTML — show a friendly, actionable message.
      const detail = e.response?.data?.detail;
      const clean = (typeof detail === "string" && detail.length < 120 && !/<|cloudflare|origin web server/i.test(detail))
        ? detail
        : "Something hiccuped — please try again in a moment.";
      toast.error("Couldn't get your share link", { description: clean });
    } finally {
      setBusy(false);
    }
  }, [busy]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={busy}
      title="Copy your referral share link"
      data-testid="header-copy-share-link"
      aria-label="Copy your referral share link"
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-fredoka font-bold tracking-wider uppercase transition-all " +
        (copied
          ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-200 shadow-md shadow-emerald-500/25"
          : "bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border-cyan-400/40 text-cyan-100 hover:from-cyan-500/35 hover:to-emerald-500/35 hover:shadow-md hover:shadow-cyan-500/25")
      }
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : copied ? <Check size={12} strokeWidth={3} /> : <Share2 size={12} strokeWidth={2.5} />}
      <span className="hidden sm:inline">{copied ? "Copied!" : "Copy Link"}</span>
    </button>
  );
}
